import {
    AuthorizationDebitRequest,
    AuthorizationLeaveCountryRequest,
    ChangeDurationMileage,
    ConfirmInformationRequest,
    ContactDetails,
    Contract,
    ContractTransferRequest,
    DeadlineRequest,
    DeathRequest,
    DocumentsRequest,
    getDocumentSendingEndpoint,
    getRequestSendingEndpoint,
    Identification,
    LossRequest,
    OtherRequest,
    ProductTypeEN,
    RegistrationCertificateRequest,
    RequestCategory,
    requestFormValidationSchema,
    RequestStatus,
    TenantChangeRequest,
    VehicleClaimRequestForBalloon,
    VehicleClaimRequestForCredit,
} from 'cpfr-common';
import { useAnalyticsActionTracker } from '@cp-shared-19/frontend-ui';
import {
    Button,
    ButtonContainer,
    ContentSection,
    Notification,
    Layout,
    Modal,
    Spinner,
} from '@vwfs-bronson/bronson-react';
import { Form, Formik } from 'formik';
import React, { useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useHistory } from 'react-router-dom';
import { CpDataApi } from '../../../cp-xhr';
import { dashboardPagePath } from '../../navigation/paths';
import {
    authorizationDebitMapper,
    changeDurationMileageMapper,
    deadlineMapper,
    deathMapper,
    documentsMapper,
    lossMapper,
    otherMapper,
    registrationCertificateDuplicateMapper,
    requestContractTransferMapper,
    tenantChangeRequestMapper,
    vehicleClaimMapper,
} from './category-selection/category-view/category-views';
import { authorizationLeaveCountryMapper } from './category-selection/category-view/category-views/authorization-leave-country';
import { CategoryView } from './category-selection/category-view/CategoryView';
import { CategorySelection } from './category-selection/CategorySelection';
import { ContractSelection } from './contract-selection/ContractSelection';
import { getInitialValues, RequestInitialValues } from './initialValues';
import { RequestFormMessages } from './validationMessages';
import { TrustedShopsIntegrationCustomType } from '@cp-shared-19/apis';
import { CustomerHappinessIndex } from '../../customer-happiness-index/CustomerHappinessIndex';
import { canSubmitForm, QuickLink, QuickLinkType } from './utils';
import QuickLinks from './QuickLinks';
import { EmailInputModal } from './email-input/EmailInputModal';
import { SubmitButton } from './SubmitButton';
import { useContactInformationChangeStatus } from 'components/my-profile/useContactInformationChangeStatus';
import { dedupeRequests, getErrorCode, isDuplicateSubmissionError, useSubmitOnce } from 'utils';

enum MessageType {
    ERROR = 'ERROR',
    SUCCESS = 'SUCCESS',
    VALIDATION_ERROR = 'VALIDATION_ERROR',
    MISSING_USER_EMAIL_ERROR = 'EMAIL_NOT_FOUND_OR_PROVIDED',
    NONE = 'NONE',
}

type Request =
    | AuthorizationDebitRequest
    | DeadlineRequest
    | VehicleClaimRequestForCredit
    | VehicleClaimRequestForBalloon
    | DeathRequest
    | LossRequest
    | ContractTransferRequest
    | DocumentsRequest
    | RegistrationCertificateRequest
    | AuthorizationLeaveCountryRequest
    | TenantChangeRequest
    | ChangeDurationMileage
    | ConfirmInformationRequest
    | OtherRequest;

export const getRequestMapper = (
    category: RequestCategory,
): ((values: RequestInitialValues) => Request | Promise<Request> | Promise<Request[]>) => {
    const mapper: {
        [key: string]: (values: RequestInitialValues) => Request | Promise<Request> | Promise<Request[]>;
    } = {
        AUTHORIZATION_DEBIT: authorizationDebitMapper,
        DEADLINE: deadlineMapper,
        VEHICLE_CLAIM: vehicleClaimMapper,
        DEATH: deathMapper,
        LOSS: lossMapper,
        REQUEST_CONTRACT_TRANSFER: requestContractTransferMapper,
        AUTHORIZATION_LEAVE_COUNTRY: authorizationLeaveCountryMapper,
        DOCUMENTS: documentsMapper,
        REGISTRATION_CERTIFICATE_DUPLICATE: registrationCertificateDuplicateMapper,
        TENANT: tenantChangeRequestMapper,
        CHANGE_DURATION_MILEAGE: changeDurationMileageMapper,
        OTHER: otherMapper,
    };
    return mapper[category];
};

export const FormView: React.FC<{
    contracts?: Contract[];
    contactDetails?: ContactDetails;
    identification?: Identification;
    isSMECustomer: boolean;
}> = ({ contracts, contactDetails, identification, isSMECustomer }) => {
    const { t } = useTranslation(['request', 'loader']);
    const { isSubmitting, run } = useSubmitOnce();
    const [messageType, setMessageType] = useState<MessageType>(MessageType.NONE);
    const history = useHistory();
    const { onAction: onSuccess } = useAnalyticsActionTracker('onRequestsSuccess');
    const { onAction: onError } = useAnalyticsActionTracker('onRequestsError');
    const [key, setKey] = useState(Math.random());
    const [selectedQuickLink, setSelectedQuickLink] = useState<QuickLink>();
    const setFieldValueRef = useRef<(field: string, value: string, shouldValidate?: boolean) => void>();
    const [showRequestForm, setShowRequestForm] = useState(false);
    const [showSuccessNotification, setShowSuccessNotification] = useState(false);
    const [defaultCategory, setDefaultCategory] = useState('');
    const [defaultType, setDefaultType] = useState('');
    const { data: contactInformationChange } = useContactInformationChangeStatus();
    const isContactChangePending = contactInformationChange?.requestCustomerStatus === RequestStatus.IN_PROGRESS;

    if (!contracts) {
        return null;
    }

    const handleSubmit = (
        values: RequestInitialValues,
        // eslint-disable-next-line
        { resetForm }: { resetForm: Function },
    ): Promise<void> =>
        // The latch covers validation and file mapping as well, not just the request:
        // those awaits are where a second click used to slip through and send the
        // very same request again.
        run(async () => {
            if (!canSubmitForm(values)) {
                return;
            }

            const isFormValid = await requestFormValidationSchema(
                RequestFormMessages(t),
                messageType === MessageType.MISSING_USER_EMAIL_ERROR,
            ).isValid(values);

            if (!isFormValid) {
                setMessageType(MessageType.VALIDATION_ERROR);
                return;
            }

            const category = values.categorySelection;
            if (!category) return;
            const requestMapper = getRequestMapper(category);
            const mappingResult = await requestMapper(values);
            const bodyRequests: Request[] = dedupeRequests(
                Array.isArray(mappingResult) ? mappingResult : [mappingResult],
            );
            const promises = bodyRequests.map((body) => {
                if (
                    values.documents.documentType !== 'Copie de mon contrat' &&
                    values.documents.documentType !== 'Echéancier' &&
                    values.documents.documentType !== 'Duplicata de facture de loyer'
                ) {
                    return CpDataApi.post(getRequestSendingEndpoint(), body);
                } else {
                    return CpDataApi.post(getDocumentSendingEndpoint(), body);
                }
            });

            const reportSubmitted = (): void => {
                setShowSuccessNotification(true);
                onSuccess(category);
                resetForm();
                setMessageType(MessageType.SUCCESS);
            };

            await Promise.all(promises)
                .then(reportSubmitted)
                .catch((error) => {
                    // A duplicate is not a failure: the request is already registered.
                    if (isDuplicateSubmissionError(error)) {
                        reportSubmitted();
                        return;
                    }
                    onError(category);
                    // Read through getErrorCode: a timeout or a network drop has no
                    // `response`, and reading into it here threw before the user was
                    // ever told the request failed.
                    const errorCode = getErrorCode(error);
                    setMessageType(
                        errorCode === MessageType.MISSING_USER_EMAIL_ERROR
                            ? MessageType.MISSING_USER_EMAIL_ERROR
                            : MessageType.ERROR,
                    );
                });
        });

    const handleModalClose = (): void => {
        setMessageType(MessageType.NONE);
        setKey(Math.random());
    };

    // eslint-disable-next-line
    const handleSuccessModalClose = (resetForm: Function): void => {
        resetForm({ values: getInitialValues(contactDetails) });
        handleModalClose();
    };

    const handleSuccessModalConfirm = (): void => {
        handleModalClose();
        history.push(dashboardPagePath() + '?success=true');
    };
    const handleSuccessMissingEmailModal = (values: RequestInitialValues): void => {
        handleSubmit(values, {
            resetForm: () => {
                return null;
            },
        });
        handleModalClose();
    };
    const handleContinueClick = (quickLink: QuickLink): void => {
        if (quickLink.id === QuickLinkType.OTHER) {
            setShowRequestForm(true);
            return;
        }

        if (quickLink.id === QuickLinkType.BILLING_COPY_REQUEST) {
            setDefaultCategory('DOCUMENTS');
            setDefaultType('Duplicata de facture de loyer');
            setFieldValueRef.current?.('documents.documentType', 'Duplicata de facture de loyer');
            setFieldValueRef.current?.('categorySelection', 'DOCUMENTS');
            setShowRequestForm(true);
            return;
        }

        if (quickLink.id === QuickLinkType.AUTHORIZATION_DEBIT) {
            setDefaultCategory('AUTHORIZATION_DEBIT');
            setFieldValueRef.current?.('categorySelection', 'AUTHORIZATION_DEBIT');
            setShowRequestForm(true);
            return;
        }

        if (quickLink.id === QuickLinkType.TAX_DOCUMENT) {
            setDefaultCategory('DOCUMENTS');
            setDefaultType('Etats fiscaux - Amortissements non déductibles/ Avantages en nature');
            setFieldValueRef.current?.(
                'documents.documentType',
                'Etats fiscaux - Amortissements non déductibles/ Avantages en nature',
            );
            setFieldValueRef.current?.('categorySelection', 'DOCUMENTS');
            setShowRequestForm(true);
            return;
        }

        if (quickLink.id === QuickLinkType.CHANGE_DURATION_MILEAGE) {
            setDefaultCategory('CHANGE_DURATION_MILEAGE');
            setFieldValueRef.current?.('categorySelection', 'CHANGE_DURATION_MILEAGE');
            setShowRequestForm(true);
            return;
        }

        history.push(quickLink.path);
    };

    const handleOnSelectQuickLink = (quickLink: QuickLink) => {
        setSelectedQuickLink(quickLink);
        setShowRequestForm(false);
        handleContinueClick(quickLink);
    };

    const handleOnHighlightQuickLink = (quickLink: QuickLink) => {
        setSelectedQuickLink(quickLink);
    };

    const renderErrorMessage = (msgType: string) => {
        switch (msgType) {
            case MessageType.ERROR:
                return 'modal-error.text';
            case MessageType.MISSING_USER_EMAIL_ERROR:
                return 'modal-missing-email-error.text';
            case MessageType.VALIDATION_ERROR:
                return 'modal-validation-error.text';
            default:
                return 'modal-error.text';
        }
    };

    return (
        <>
            {isSubmitting && (
                <Spinner center>
                    <p>{t('genericText', { ns: 'loader' })}</p>
                </Spinner>
            )}
            {showSuccessNotification && (
                <Notification
                    title={t('success-notification.title')}
                    className="u-mb"
                    visible
                    testId="contract-success-notification"
                    showCloseButton={false}
                    status={'success'}
                >
                    <span
                        dangerouslySetInnerHTML={{
                            __html: t('success-notification.text'),
                        }}
                    />
                </Notification>
            )}
            <Formik
                enableReinitialize
                initialValues={getInitialValues(contactDetails, false, identification)}
                validationSchema={requestFormValidationSchema(
                    RequestFormMessages(t),
                    messageType === MessageType.MISSING_USER_EMAIL_ERROR,
                )}
                onSubmit={handleSubmit}
                validateOnBlur={true}
            >
                {({ values, resetForm, setFieldValue }): JSX.Element => {
                    setFieldValueRef.current = setFieldValue;
                    return (
                        <Form onSubmit={(e): void => e.preventDefault()} data-testid="request-form">
                            <ContentSection className={'request-certificate-form'} key={key}>
                                <div className="o-fieldset">
                                    <div className="o-fieldset__row">
                                        <Layout>
                                            <Layout.Item>
                                                <ContractSelection
                                                    contracts={contracts}
                                                    currentContracts={values.contractsSelection}
                                                    isSMECustomer={isSMECustomer}
                                                />
                                            </Layout.Item>
                                            {values.contractsSelection?.length > 0 && (
                                                <QuickLinks
                                                    isSMECustomer={isSMECustomer}
                                                    onHighlightQuickLink={handleOnHighlightQuickLink}
                                                    onConfirmQuickLink={handleOnSelectQuickLink}
                                                    selectedQuickLink={selectedQuickLink}
                                                    selectedContracts={values.contractsSelection}
                                                />
                                            )}
                                        </Layout>
                                    </div>
                                    {showRequestForm && isContactChangePending && (
                                        <div className="o-fieldset__row">
                                            <Layout>
                                                <Layout.Item>
                                                    <Notification
                                                        title={''}
                                                        hideTitle={true}
                                                        className="u-mb"
                                                        visible
                                                        testId="pending-change-forms-notification"
                                                        showCloseButton={false}
                                                        status={'warning'}
                                                    >
                                                        <p
                                                            dangerouslySetInnerHTML={{
                                                                __html: t(
                                                                    'category-selection.pending-contact-information-change',
                                                                ),
                                                            }}
                                                        />
                                                    </Notification>
                                                </Layout.Item>
                                            </Layout>
                                        </div>
                                    )}
                                    {showRequestForm && !isContactChangePending && (
                                        <div className="o-fieldset__row">
                                            <Layout>
                                                <Layout.Item>
                                                    <CategorySelection
                                                        contractsSelection={values.contractsSelection}
                                                        resetForm={resetForm}
                                                        currentCategorySelection={
                                                            values.categorySelection || defaultCategory
                                                        }
                                                    />
                                                </Layout.Item>
                                                <CategoryView
                                                    contractsSelected={values.contractsSelection}
                                                    category={
                                                        (values.categorySelection as RequestCategory) || defaultCategory
                                                    }
                                                    productTypeEn={
                                                        values.contractsSelection.length !== 1
                                                            ? ProductTypeEN.OTHER
                                                            : values.contractsSelection[0].productTypeEN
                                                    }
                                                    productType={
                                                        values.contractsSelection.length !== 1
                                                            ? ProductTypeEN.OTHER
                                                            : values.contractsSelection[0].productType
                                                    }
                                                    bundledProducts={
                                                        values.contractsSelection.length === 1
                                                            ? values.contractsSelection[0].bundledProducts
                                                            : undefined
                                                    }
                                                    identification={identification}
                                                    isSMECustomer={isSMECustomer}
                                                    defaultType={defaultType}
                                                />
                                            </Layout>
                                        </div>
                                    )}
                                    {showRequestForm && !isContactChangePending && (
                                        <div className="o-fieldset__row">
                                            <ButtonContainer center>
                                                <SubmitButton
                                                    onSubmit={({ dirty, isValid }) => {
                                                        if (!dirty || !isValid) {
                                                            setMessageType(MessageType.VALIDATION_ERROR);
                                                        }
                                                    }}
                                                />
                                            </ButtonContainer>
                                        </div>
                                    )}
                                    <Button
                                        element="a"
                                        secondary
                                        link
                                        small
                                        icon="semantic-back"
                                        testId={'dashboard-button'}
                                        type="button"
                                        aria-label={t('aria-title')}
                                        title={t('aria-title')}
                                        onClick={(): void => {
                                            history.push(dashboardPagePath());
                                        }}
                                    >
                                        {t('translation:editable-section-nav.dashboard')}
                                    </Button>
                                </div>
                            </ContentSection>
                            <EmailInputModal
                                show={messageType === MessageType.MISSING_USER_EMAIL_ERROR}
                                onClose={handleModalClose}
                                onConfirm={() => handleSuccessMissingEmailModal(values)}
                            />
                            <Modal
                                shown={[MessageType.VALIDATION_ERROR, MessageType.ERROR].includes(messageType)}
                                status="error"
                                onClose={handleModalClose}
                                onCancel={handleModalClose}
                                onClickOutside={handleModalClose}
                                buttonCancelText={t('translation:editable-section-nav.back')}
                                testId={'error-modal'}
                                title=""
                            >
                                {t(renderErrorMessage(messageType))}
                            </Modal>
                            <Modal
                                shown={messageType === MessageType.SUCCESS}
                                status="success"
                                title={t('modal-success.headline')}
                                onClose={(): void => handleSuccessModalClose(resetForm)}
                                onConfirm={handleSuccessModalConfirm}
                                onClickOutside={(): void => handleSuccessModalClose(resetForm)}
                                buttonConfirmText={t('translation:editable-section-nav.dashboard')}
                                testId={'success-modal'}
                                role="status"
                                aria-live="polite"
                                aria-atomic="true"
                            >
                                {t('modal-success.text')}
                            </Modal>
                        </Form>
                    );
                }}
            </Formik>
            <CustomerHappinessIndex
                firstName={identification?.data?.name}
                lastName={identification?.data?.surname}
                email={contactDetails?.email}
                customType={TrustedShopsIntegrationCustomType.CUSTOMERPORTAL_REQUEST}
            />
        </>
    );
};
